import React, { useMemo } from 'react'
import numeral from 'numeral'
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { useSelector } from 'react-redux'

import Table from '../../../Invoices/Table'
import MarginCell from '../MarginCell'

import styles from './styles.module.scss'
import { getCurrency } from '../../../../helpers/currencies'

const COST_COLORS = ["#6366f1", "#f59e0b"]
const REVENUE_COLOR = "#10b981"
const MIN_ITEM_WIDTH = 96
const MIN_CHART_WIDTH = 720
const X_AXIS_MAX_LABEL_LENGTH = 12

const columnHelper = createColumnHelper()

const formatXAxisLabel = value => {
  const label = String(value || '')
  if (label.length <= X_AXIS_MAX_LABEL_LENGTH) {
    return label
  }
  return `${label.slice(0, X_AXIS_MAX_LABEL_LENGTH - 1)}…`
}

const Placeholder = () => {
  const { t } = useTranslation()
  return (
    <div>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={24}
        height={24}
        fill="none"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        aria-hidden="true"
        className="lucide lucide-briefcase h-8 w-8"
      >
        <path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
        <rect width={20} height={14} x={2} y={6} rx={2} />
      </svg>
      <div style={{color: '#333945', marginBottom : 4}}>{t('No project activity')}</div>
      <div>{t('Pick a period to view')}</div>
    </div>
  )
}

const ProfitabilityPlaces = ({data}) => {
  const { t } = useTranslation()
  const isNotEmptyData = data.length > 0 && data.some(item => numeral(item.issued_invoices_amount).value() > 0 || numeral(item.total_expenses_amount).value() > 0 || numeral(item.total_work_times_cost).value() > 0)
  const chartData = isNotEmptyData ? data.sort((a, b) => numeral(b.issued_invoices_amount).value() - numeral(a.issued_invoices_amount).value()) : []
  const chartWidth = Math.max(chartData.length * MIN_ITEM_WIDTH, MIN_CHART_WIDTH)
  

  const companyInfo = useSelector(state => state.company.companyInfo)
  const currency = getCurrency(companyInfo?.currency)

  const columns = useMemo(() => ([
    columnHelper.accessor('place_name', {
      id: 'place_name',
      header: t('Project'),
      enableSorting: false,
      cell: ({ row }) => {
        return row.original.place_name
      }
    }),
    columnHelper.accessor('total_hours', {
      id: 'total_hours',
      enableSorting: false,
      cell: ({ getValue }) => numeral(getValue() || 0).format('0.00'),
      header: t('Hours'),
    }),
    columnHelper.accessor('total_work_times_cost', {
      id: 'total_work_times_cost',
      enableSorting: false,
      cell: ({ getValue }) => `${numeral(getValue() || 0).format('0,0.00')} ${currency.symbol}`,
      header: t('Work times'),
    }),
    columnHelper.accessor('total_expenses_amount', {
      id: 'total_expenses_amount',
      enableSorting: false,
      cell: ({ getValue }) => `${numeral(getValue() || 0).format('0,0.00')} ${currency.symbol}`,
      header: t('Expenses'),
    }),
    columnHelper.display({
      id: 'total_cost',
      enableSorting: false,
      cell: ({ row }) => `${numeral(row.original.total_expenses_amount + row.original.total_work_times_cost).format('0,0.00')} ${currency.symbol}`,
      header: t('Total cost'),
    }),
    columnHelper.accessor('issued_invoices_amount', {
      id: 'issued_invoices_amount',
      enableSorting: false,
      cell: ({ getValue }) => `${numeral(getValue() || 0).format('0,0.00')} ${currency.symbol}`,
      header: t('Revenue'),
    }),
    columnHelper.display({
      id: 'profit',
      enableSorting: false,
      cell: ({ row }) => {
        const value = numeral(row.original.issued_invoices_amount - row.original.total_expenses_amount - row.original.total_work_times_cost)
        const color = value.value() > 0 ? '#10b981' : value.value() < 0 ? '#ef4444' : '#374151'
        return <span style={{color: color}}>{`${value.format('0,0.00')} ${currency.symbol}`}</span>
      },
      header: t('Profit'),
    }),
    columnHelper.display({
      id: 'margin',
      enableSorting: false,
      cell: ({ row }) => {
        const totalCost = row.original.total_expenses_amount + row.original.total_work_times_cost
        const value = numeral(row.original.issued_invoices_amount).value() ? (row.original.issued_invoices_amount - totalCost) / row.original.issued_invoices_amount * 100 : 0
        return <MarginCell value={value} />
      },
      header: t('Margin'),
    }),
  ]), [t])

  const table = useReactTable({
    data: chartData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => String(row.place_id),
  })

  return (
    <div className={styles.container}>
      {
        isNotEmptyData
          ? <div className={styles.chartScroll}>
              <div className={styles.chart} style={{width: chartWidth}}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData} margin={{ top: 5, right: 12, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="currentColor" style={{opacity: 0.2}} />
                    <XAxis
                      dataKey="place_name"
                      stroke="currentColor"
                      fontSize={12}
                      interval={0}
                      minTickGap={0}
                      tickMargin={8}
                      tickFormatter={formatXAxisLabel}
                    />
                    <YAxis stroke="currentColor" fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip
                      contentStyle={{ background: '#fff', color: '#333945', border: '1px solid #CBD1D8', borderRadius: 8, fontSize: 12 }}
                      formatter={(v) => numeral(v).format('0.00')}
                    />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar dataKey="total_work_times_cost" name={t('Work entries')} stackId="cost" fill={COST_COLORS[0]} radius={[0, 0, 0, 0]} />
                    <Bar dataKey="total_expenses_amount" name={t('Expenses')} stackId="cost" fill={COST_COLORS[1]} radius={[4, 4, 0, 0]} />
                    <Bar dataKey="issued_invoices_amount" name={t('Revenue')} fill={REVENUE_COLOR} radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          : null
      }
      <div style={{paddingTop: 16}}>
        <Table
          table={table}
          columns={columns}
          placeholder={<Placeholder />}
          headerClassName={styles.tableHeader}
          tableClassName={styles.table} />
      </div>
    </div>
  )
}

export default ProfitabilityPlaces
